import { useMemo } from 'react'
import { useRecoilValue } from 'recoil'
import { useTranslation } from 'react-i18next'
import { selectedPageAtom } from '@/atoms/editorAtoms'
import {
  baseLayoutAtom,
  baseLayoutMarginsSelector,
  editorPagesAtom,
} from '@/atoms/_editorAtoms'
import { bleedFoldMarginsFor, checkBleedOverlap } from '@messenger/utils'
import type { GrowthCeilingKept } from '@messenger/utils'
import { isEditorTextBoxItemType } from '@/utils/editor-text-box/editorTextBoxAdapter'
import type { Content } from '@messenger/types'

type ItemInkRect = {
  top: number
  left: number
  bottom: number
  right: number
}

/**
 * What a box's load result did with its text, as far as a warning needs:
 * whether the text was made smaller to fit the row's frame; what bounds the
 * room under the box — an item's id, `margin`, `fold`, `preexisting` (the
 * stored frame is past that room: a box saved over what is below it) or
 * null (the page bottom alone), with `kept` naming the room and what bounds
 * it for a flagged frame; and whether the text still runs past the box at
 * its smallest size, the last resort. The frame itself never moves.
 */
export type LoadResultForWarning = {
  fitted: boolean
  boundedBy: string | null
  overflowing?: boolean
  kept?: GrowthCeilingKept | null
}

export type LoadWarning = {
  boundedBy: string | null
  /** The phrase (`editor.loadWarning.<key>`): that the text does not fit even at its smallest size. The overlay carries it in its control state and paints no badge for it. */
  key: string
  message: string
}

type UseBleedWarningParams = {
  itemX: number
  itemY: number
  itemType?: string
  inkRect?: ItemInkRect | null
  contentEmpty?: boolean
  loadResult?: LoadResultForWarning | null
}

type UseBleedWarningResult = {
  showWarning: boolean
  bleedType: 'edge' | 'center' | null
  warningLocation: string | null
  warningMessage: string
  /**
   * Set when the text does not fit the box even at its smallest size.
   * Independent of the margin warning — a box can be overflowing and bleeding
   * at once, and the badge says both.
   */
  loadWarning: LoadWarning | null
}

/**
 * The phrase key for a load result: `overflowing` when the text does not fit
 * even at its smallest size, and null for everything else.
 *
 * It used to say more. On Jackson's instruction after dogfooding, the badge
 * no longer speaks for text made smaller to fit, nor for a stored frame past
 * the room under it — whether what it was saved over was the text or the QR
 * code below it, any other item, the bottom margin or the fold. Those fired
 * on every load while he was editing and told him nothing he had not just
 * done.
 * `overflowing` stays because it is the one thing nobody can see for
 * themselves — the text is clipped at the frame, at its smallest size, and
 * there is no more room to give it.
 *
 * None of this changes what is recorded. Every case logs through
 * `logTextBoxLoadResult` (`utils/editor/textBoxLoadLog.ts`) at info in
 * Datadog, and the PDF job still lists the flagged rows. The key reaches the
 * overlay's control state and, on Jackson's ruling, paints no badge there
 * (`WarningIndicators.tsx`): a text box shows the margin-or-fold overlap icon
 * and a Lotus box's own overflow band, and no other warning.
 */
export const loadWarningKeyOf = (
  result: LoadResultForWarning,
): string | null => (result.overflowing === true ? 'overflowing' : null)

export const useBleedWarning = ({
  itemX,
  itemY,
  itemType,
  inkRect,
  contentEmpty,
  loadResult = null,
}: UseBleedWarningParams): UseBleedWarningResult => {
  const { t } = useTranslation()
  const bleeds = useRecoilValue(baseLayoutMarginsSelector)
  const layout = useRecoilValue(baseLayoutAtom)
  const pages = useRecoilValue(editorPagesAtom) as Content[]
  const selectedPage = useRecoilValue(selectedPageAtom)

  const currentPage = pages[selectedPage - 1] ?? null
  const pageWidth = currentPage?.product?.productSize?.width ?? 0
  const pageHeight = currentPage?.product?.productSize?.height ?? 0
  const padding = currentPage?.product?.padding ?? null
  // A trifold is judged against the selected page's own folds, placed by the
  // page's JSON number as the canvas draws them; any other product gets
  // undefined and keeps its center margins. A page with no JSON number, which
  // the canvas draws no folds for, is judged at its position instead.
  const pageNumber = currentPage?.pageProperties?.pageNumber ?? selectedPage
  const foldMargins = useMemo(
    () => bleedFoldMarginsFor(layout, pageNumber, pageWidth),
    [layout, pageNumber, pageWidth],
  )

  const overlapResult = useMemo(() => {
    // An empty box has no ink to judge. A locked box is judged like any
    // other: its read-only fit publishes its ink.
    if (contentEmpty) {
      return {
        isOverlapping: false,
        bleedType: null,
        location: null,
      }
    }

    // A text box is the only kind a margin warning speaks for.
    if (!isEditorTextBoxItemType(itemType)) {
      return {
        isOverlapping: false,
        bleedType: null,
        location: null,
      }
    }

    // A text box is judged by its ink alone: the frame may sit in a margin
    // while every glyph stays clear. Until the ink is measured there is
    // nothing to judge.
    if (inkRect == null)
      return { isOverlapping: false, bleedType: null, location: null }
    return checkBleedOverlap(
      itemX + inkRect.left,
      itemY + inkRect.top,
      Math.max(0, inkRect.right - inkRect.left),
      Math.max(0, inkRect.bottom - inkRect.top),
      bleeds,
      pageWidth,
      pageHeight,
      padding,
      foldMargins,
    )
  }, [
    contentEmpty,
    itemX,
    itemY,
    bleeds,
    pageWidth,
    pageHeight,
    padding,
    foldMargins,
    itemType,
    inkRect,
  ])

  const warningMessage = useMemo(() => {
    if (!overlapResult.isOverlapping) return ''

    if (overlapResult.bleedType === 'center') {
      return t('editor.bleedWarning.centerBleed')
    }

    return t('editor.bleedWarning.edgeBleed')
  }, [overlapResult.isOverlapping, overlapResult.bleedType, t])

  const loadWarning = useMemo((): LoadWarning | null => {
    // The only sentence left is `overflowing`. A stored frame past its room is
    // still flagged, still logged and still in the PDF job's record; it simply
    // no longer paints a badge, so the neighbour that stopped the box is no
    // longer looked up here.
    if (loadResult === null) return null
    const key = loadWarningKeyOf(loadResult)
    if (key === null) return null
    return {
      boundedBy: loadResult.boundedBy,
      key,
      message: t(`editor.loadWarning.${key}`),
    }
  }, [loadResult, t])

  return {
    showWarning: overlapResult.isOverlapping,
    bleedType: overlapResult.bleedType,
    warningLocation: overlapResult.location,
    warningMessage,
    loadWarning,
  }
}
